import NavMenuButton from "./NavMenuButton"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react"
import {
  FolderIcon,
  ListBulletIcon,
  UsersIcon,
  EllipsisHorizontalCircleIcon,
  ChartBarIcon,
  ChartPieIcon,
  // AcademicCapIcon,
} from "@heroicons/react/24/outline"
import UserMenu from "./UserMenu"
import {
  Fragment,
  ReactElement,
  cloneElement,
  isValidElement,
  useEffect,
  useState,
} from "react"
import Button from "./Button"
import clsx from "clsx"
import {
  Cog6ToothIcon,
  InformationCircleIcon,
  ChartBarIcon as ChartBarIconSolid,
  ArrowRightStartOnRectangleIcon,
} from "@heroicons/react/20/solid"
import { Modals, useModals } from "@/hooks/useModals"
import { Link, router } from "@inertiajs/react"
import usePathname from "@/hooks/usePathname"
import useUser from "@/hooks/useUser"
import Spinner from "./Spinner"
import Logo from "./Logo"

const navigation = [
  { name: "Games", href: "/games", icon: <ListBulletIcon /> },
  { name: "Events", href: "/events", icon: <FolderIcon /> },
  { name: "Players", href: "/players", icon: <UsersIcon /> },
  { name: "My Stats", href: "/stats", icon: <ChartPieIcon />, badge: "New" },
  // { name: "Puzzles", href: "/puzzles/me", icon: <AcademicCapIcon /> },
  { name: "Admin", href: "/admin", icon: <ChartBarIcon />, admin: true },
]

const navigationItemsCount = navigation.length

export const AppSidebar = ({ guestMode = false }: { guestMode?: boolean }) => {
  const { isAuthenticated, isAdmin } = useUser()
  const pathname = usePathname()

  return (
    <>
      <div className="hidden xl:fixed xl:inset-y-0 xl:z-50 xl:flex xl:w-64 xl:flex-col">
        <div className="relative flex grow flex-col overflow-y-auto border-r border-gray-200 bg-white px-6 pt-4 xl:pt-0">
          <div className="flex shrink-0 items-center px-4 py-4 sm:h-18 sm:w-auto">
            <Link href="/">
              <span className="sr-only">ChessDrop</span>
              <Logo heightClassName="h-6" />
            </Link>
          </div>

          <div className="flex flex-1">
            {isAuthenticated ? (
              <div className="flex flex-1 flex-col justify-between pt-6">
                <nav className="flex flex-1 flex-col">
                  <ul role="list" className="flex flex-1 flex-col gap-y-2">
                    {navigation.map((item) => {
                      if (item.admin && !isAdmin) return null

                      return (
                        <li key={item.name}>
                          <NavMenuButton {...item} />
                        </li>
                      )
                    })}
                  </ul>
                </nav>

                <UserMenu />
              </div>
            ) : (
              <div>
                <div className="rounded-2xl bg-gray-800 p-4">
                  <div className="text-center text-sm text-white">
                    {guestMode
                      ? "Record your game by making moves on the board. When you're done, hit Save to create your free account and keep your game."
                      : "ChessDrop is your personal, online chess database. Record, access, analyze and share your favorite games and puzzles—anytime, anywhere!"}
                  </div>
                  {!guestMode && (
                    <Button fullWidth href="/signup" className="mt-5">
                      Join ChessDrop
                    </Button>
                  )}
                </div>

                {!guestMode && (
                  <Button
                    fullWidth
                    style="tertiary"
                    href={`/login?return_to=${encodeURIComponent(pathname)}`}
                    className="mt-2"
                  >
                    Log in
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

const TabItem = ({
  name,
  href,
  icon,
}: {
  name: string
  href: string
  icon: ReactElement
}) => {
  const pathname = usePathname()
  const current = pathname === href

  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const removeStartEventListener = router.on("start", (event) => {
      if (event.detail.visit.url.pathname === href) {
        setIsLoading(true)
      }
    })

    const removeFinishEventListener = router.on("finish", () => {
      setIsLoading(false)
    })

    return () => {
      removeStartEventListener()
      removeFinishEventListener()
    }
  }, [href])

  const iconWithClasses = isValidElement(icon)
    ? cloneElement(icon as ReactElement<any>, {
        className: "h-5 w-5 shrink-0",
        "aria-hidden": true,
      })
    : null

  return (
    <div key={name} className="flex justify-center">
      <Link
        href={href}
        className={clsx(
          "group pb-safe-or-3.5 relative flex w-full max-w-36 flex-col items-center justify-center gap-y-1 overflow-hidden pt-3.5 focus-visible:outline-none",
          current
            ? "text-gray-900"
            : "text-gray-500 hover:text-gray-900 focus-visible:text-gray-900"
        )}
      >
        {current && (
          <div className="bg-chessdrop-500 absolute top-0 h-1.5 w-5 -translate-y-1/2 rounded-full" />
        )}
        <div className="flex size-5 shrink-0 items-center justify-center">
          {isLoading ? (
            <Spinner size="xs" className="text-gray-500" />
          ) : (
            iconWithClasses
          )}
        </div>
        <span
          className={clsx(
            "text-xs leading-none",
            current ? "font-semibold" : "font-medium"
          )}
        >
          {name}
        </span>
      </Link>
    </div>
  )
}

export const AppTabs = () => {
  const { isAuthenticated, isAdmin, signOut } = useUser()
  const { openModal } = useModals()
  const pathname = usePathname()

  return (
    <div
      className={clsx(
        "sticky bottom-0 z-10 grid border-t border-gray-200 bg-white px-2 xl:hidden",
        {
          "grid-cols-4": navigationItemsCount === 4,
          "grid-cols-5": navigationItemsCount === 5,
        }
      )}
    >
      {navigation
        .filter((item) => !item.admin)
        .map((item) => (
          <TabItem key={item.name} {...item} />
        ))}

      <Menu className="relative flex justify-center" as="div">
        {({ open }) => (
          <>
            <MenuButton
              className={clsx(
                "group pb-safe-or-3.5 relative flex w-full max-w-36 flex-col items-center justify-center gap-y-1 overflow-hidden pt-3.5 focus-visible:outline-none",
                open
                  ? "text-gray-900"
                  : "text-gray-500 hover:text-gray-900 focus-visible:text-gray-900"
              )}
            >
              {open && (
                <div className="bg-chessdrop-500 absolute top-0 h-1.5 w-5 -translate-y-1/2 rounded-full" />
              )}
              <EllipsisHorizontalCircleIcon
                className="h-5 w-5 shrink-0"
                aria-hidden="true"
              />
              <span
                className={clsx(
                  "text-xs leading-none",
                  open ? "font-semibold" : "font-medium"
                )}
              >
                More
              </span>
            </MenuButton>

            <MenuItems
              transition
              className={clsx([
                "absolute right-6 bottom-full z-10 mt-2 w-40 origin-bottom-right -translate-y-2 overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/5 focus:outline-none md:right-1/2 md:origin-bottom md:translate-x-1/2",
                "data-closed:scale-95 data-closed:opacity-0",
                "data-enter:duration-100 data-enter:ease-out",
                "data-leave:duration-75 data-leave:ease-in",
              ])}
            >
              <div className="py-2">
                {isAuthenticated ? (
                  <>
                    <MenuItem
                      as="button"
                      onClick={signOut}
                      className="flex w-full items-center gap-x-2 px-3 py-2 text-left text-sm"
                    >
                      <ArrowRightStartOnRectangleIcon className="h-4 w-4 text-gray-400" />
                      Log out
                    </MenuItem>
                    <MenuItem
                      as="button"
                      onClick={() => openModal(Modals.Help)}
                      className="flex w-full items-center gap-x-2 px-3 py-2 text-left text-sm"
                    >
                      <InformationCircleIcon className="h-4 w-4 text-gray-400" />
                      Help & Support
                    </MenuItem>
                    <MenuItem as={Fragment}>
                      <Link
                        href="/settings"
                        className="flex w-full items-center gap-x-2 px-3 py-2 text-left text-sm"
                      >
                        <Cog6ToothIcon className="h-4 w-4 text-gray-400" />
                        Settings
                      </Link>
                    </MenuItem>
                    {isAdmin && (
                      <MenuItem as={Fragment}>
                        <Link
                          href="/admin"
                          className="flex w-full items-center gap-x-2 px-3 py-2 text-left text-sm"
                        >
                          <ChartBarIconSolid className="h-4 w-4 text-gray-400" />
                          Admin
                        </Link>
                      </MenuItem>
                    )}
                  </>
                ) : (
                  <>
                    <MenuItem as={Fragment}>
                      <Link
                        href={`/login?return_to=${encodeURIComponent(pathname)}`}
                        className="flex w-full items-center justify-center px-3 py-2 text-center text-sm"
                      >
                        Log in
                      </Link>
                    </MenuItem>
                    <MenuItem as={Fragment}>
                      <Link
                        href="/signup"
                        className="flex w-full items-center justify-center px-3 py-2 text-center text-sm"
                      >
                        Sign up
                      </Link>
                    </MenuItem>
                  </>
                )}
              </div>
            </MenuItems>
          </>
        )}
      </Menu>
    </div>
  )
}
